import { Component, type ReactNode } from 'react';
import { reportClientError } from '../lib/error-report';

/**
 * Catches render-time errors anywhere below it in the tree so a buggy
 * component doesn't white-screen the whole app + force a re-login.
 *
 * George reported "the system went dark and I had to relog in again"
 * after clicking + Add line on the invoice form. React 18 unmounts the
 * whole tree on an uncaught error; without a boundary the user sees a
 * blank page and the natural recovery is "reload + sign in". This
 * boundary shows a recover panel + lets the user retry or go back to
 * the dashboard, no relogin required.
 */
type Props = { children: ReactNode };
type State = { err: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught', err, info.componentStack);
    // Relay to Google Cloud Error Reporting via the API (DSN-free).
    reportClientError(err, { kind: 'react', componentStack: info.componentStack ?? undefined });
  }

  reset = () => this.setState({ err: null });

  override render() {
    if (!this.state.err) return this.props.children;
    return (
      <div
        style={{
          maxWidth: 560,
          margin: '4rem auto',
          padding: '2rem',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: '#6e6c66',
            letterSpacing: 1.5,
            marginBottom: 8,
          }}
        >
          KEOBOOKS
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1e3a5f', margin: '0 0 8px' }}>
          Something went wrong on this page
        </h1>
        <p style={{ color: '#5f5e5a', margin: '0 0 20px', fontSize: 14, lineHeight: 1.55 }}>
          Your session is still active. Click below to try again or head back to the dashboard.
          {process.env.NODE_ENV !== 'production' && this.state.err.message
            ? ` (${this.state.err.message})`
            : ''}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            onClick={this.reset}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid #1f7a3f',
              background: '#1f7a3f',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <a
            href="/dashboard"
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid #d3d1c7',
              color: '#1e3a5f',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            Back to dashboard
          </a>
        </div>
      </div>
    );
  }
}
